import { AnalyticsPageContext } from "layouts/AnalyticsPageContext";
import { type IListPageLayoutProps, ListPageLayout } from "layouts/ListPageLayout";
import { type ComponentType, type ReactNode, useContext, useState } from "react";
import { Route } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { BuiTokenScreenLg } from "@btnet/blueprint-design-tokens";
import { IconSideDrawer } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTShellLayout, type IShellNavItem } from "@btnet/core-components/BTShellLayout";
import { BTVerticalNav } from "@btnet/core-components/BTVerticalNav";

import { BTLinkSpaRoutingProvider } from "helpers/globalContext/BTLinkSpaRoutingProvider";
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { DefaultEnabledSpaInfo, SpaInfoContext } from "helpers/globalContext/SPAInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { useTracking } from "utilities/analytics/analytics";
import { getJobPickerState, getSelectedJobId } from "utilities/jobPicker/jobPicker";

import { BTLogo } from "commonComponents/btWrappers/BTLogo/BTLogo";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { InfoArchOptInToggle } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/InfoArchOptInToggle";
import { useInfoArchOptIn } from "commonComponents/utilities/MainNavigation/InfoArchOptInToggle/useInfoArchOptIn";

import { type IResearchPreviewPageProps } from "./ResearchPreviewLayout.types";
import { ShellNavContext } from "./ShellNavContext";

/**
 * The entities available in research preview, as the sidebar lists them. Add one here when its
 * list route gains a `researchComponent`.
 */
const RESEARCH_PREVIEW_NAV_ITEMS: IShellNavItem[] = [
    { key: "bills", label: "Bills", group: "Financials" },
];

/**
 * The providers `ListPageLayoutContext` puts around its page, so a page gets the same context in
 * the shell. `ListPageLayout`'s `AppProvider` is left out: the root `AppProvider` around
 * `AppRoutes` already supplies it.
 */
const ShellProviders = ({ children }: { children: ReactNode }) => (
    <FocusProvider shareFocusWithParent>
        <SpaInfoContext.Provider value={DefaultEnabledSpaInfo}>
            <BTLinkSpaRoutingProvider>{children}</BTLinkSpaRoutingProvider>
        </SpaInfoContext.Provider>
    </FocusProvider>
);

/**
 * Builds the props `ListPageLayout` would pass its page. The shell has no job picker, so these
 * come synchronously from the selection the v1 picker persisted.
 *
 * Unlike `ListPageLayout`, a single job's `jobName` is empty (All Jobs uses `allJobsText`) and
 * the builder's General job is never included. With no selection the page still renders, and
 * shows its own "select a jobsite" state.
 */
const useShellListPageChildProps = (allJobsText?: string): IResearchPreviewPageProps => {
    const builderInfo = useContext(BuilderInfoContext);
    const userInfo = useContext(UserInfoContext);
    const builderId = builderInfo?.builderId ?? 0;

    // Drop the AllJobs (0) and NoJobs (-1) sentinels; All Jobs persists every selected id.
    const { selectedJobIds, isAllJobsSelected, isTemplateMode } = getJobPickerState();
    let jobIds = selectedJobIds.filter((id) => id > 0);
    if (jobIds.length === 0) {
        const selectedJobId = getSelectedJobId(builderId);
        if (selectedJobId > 0) {
            jobIds = [selectedJobId];
        }
    }

    return {
        builderId,
        jobIds,
        jobName: isAllJobsSelected ? (allJobsText ?? "All Jobs") : "",
        userId: userInfo?.globalUserId ?? 0,
        isTemplateMode,
        loginType: userInfo?.loginType ?? BTLoginTypes.BUILDER,
        jobPickerReady: true,
        isAllJobsSelected,
        jobPickerCount: jobIds.length,
        setJobPickerCollapsed: () => undefined,
    };
};

interface IShellCanvasProps {
    researchComponent: ComponentType<IResearchPreviewPageProps>;
    allJobsText?: string;
}

const ShellCanvas = ({ researchComponent: ResearchComponent, allJobsText }: IShellCanvasProps) => {
    const listLayoutProps = useShellListPageChildProps(allJobsText);
    return <ResearchComponent {...listLayoutProps} />;
};

interface INavHeaderProps {
    onCollapse: () => void;
}

const NavHeader = ({ onCollapse }: INavHeaderProps) => (
    <div className="flex items-center justify-between p-5 pb-0">
        <BTLogo width={18} />
        <BTButton
            type="tertiary"
            data-testid="nav-collapse"
            aria-label="Collapse"
            icon={<IconSideDrawer />}
            onClick={onCollapse}
        />
    </div>
);

const ShellSidebar = ({
    navItems,
    selectedKey,
    onSelectKey,
    onCollapse,
}: {
    navItems: IShellNavItem[];
    selectedKey: string;
    onSelectKey: (key: string) => void;
    onCollapse: () => void;
}) => (
    <BTVerticalNav
        data-testid="research-preview-sidebar"
        navItems={navItems}
        selectedKey={selectedKey}
        onSelectKey={onSelectKey}
        top={<NavHeader onCollapse={onCollapse} />}
        bottom={
            <InfoArchOptInToggle
                label="Research preview"
                labelClassName="text-fg-secondary opacity-100"
                testIdPrefix="researchPreviewSidebarOptIn"
            />
        }
    />
);

interface IResearchPreviewShellProps
    extends Pick<IListPageLayoutProps, "path" | "exact" | "allJobsText" | "analyticsPageName"> {
    researchComponent: ComponentType<IResearchPreviewPageProps>;
}

/** The research-preview route: `researchComponent` inside `BTShellLayout` and its left nav. */
const ResearchPreviewShell = ({
    path,
    exact,
    allJobsText,
    analyticsPageName,
    researchComponent,
}: IResearchPreviewShellProps) => {
    const builderId = useContext(BuilderInfoContext)?.builderId;
    const { Track } = useTracking({
        // Same job value `ListPageLayout` tracks: one job's id, or `AllJobs`/`NoJobs`.
        jobId: builderId ? getSelectedJobId(builderId) : undefined,
        builderId,
    });
    const belowLgBreakpoint = useMediaQuery(`(max-width: ${BuiTokenScreenLg})`);
    const [selectedNavKey, setSelectedNavKey] = useState(RESEARCH_PREVIEW_NAV_ITEMS[0].key);
    const [leftNavOpen, setLeftNavOpen] = useState(true);

    return (
        <Route path={path} exact={exact}>
            <Track>
                <AnalyticsPageContext pageName={analyticsPageName}>
                    <ShellProviders>
                        <BTShellLayout
                            data-testid="research-preview-shell"
                            className="research-preview-shell"
                            sidebar={
                                !belowLgBreakpoint && leftNavOpen ? (
                                    <ShellSidebar
                                        navItems={RESEARCH_PREVIEW_NAV_ITEMS}
                                        selectedKey={selectedNavKey}
                                        onSelectKey={setSelectedNavKey}
                                        onCollapse={() => setLeftNavOpen(false)}
                                    />
                                ) : undefined
                            }
                            // No bottombar for now (ado-290557): this design has no horizontal nav,
                            // only vertical. BTShellLayout keeps the slot — pass one here once there
                            // are real mobile destinations.
                        >
                            <ShellNavContext.Provider
                                value={{
                                    // Below `lg` there is no sidebar to reopen (it never renders
                                    // regardless of `leftNavOpen`, and there's no bottombar fallback
                                    // either) — report "open" there too so a page never offers a
                                    // reopen affordance with nothing behind it, e.g. narrowing below
                                    // lg after collapsing on desktop.
                                    isLeftNavOpen: belowLgBreakpoint || leftNavOpen,
                                    openLeftNav: () => setLeftNavOpen(true),
                                }}
                            >
                                <ShellCanvas
                                    researchComponent={researchComponent}
                                    allJobsText={allJobsText}
                                />
                            </ShellNavContext.Provider>
                        </BTShellLayout>
                    </ShellProviders>
                </AnalyticsPageContext>
            </Track>
        </Route>
    );
};

interface IResearchPreviewLayoutProps extends IListPageLayoutProps {
    /**
     * The page opted-in users see inside `BTShellLayout`'s canvas slot. Omit it and every user
     * gets `ListPageLayout` with `component`, e.g. while a feature flag gates the port.
     */
    researchComponent?: ComponentType<IResearchPreviewPageProps>;
}

/**
 * A `ListPageLayout` route with a research-preview (info-arch) alternative. Users opted in to
 * research preview get `researchComponent` in the `BTShellLayout` shell; everyone else gets
 * `ListPageLayout` with `component`.
 */
export const ResearchPreviewLayout = ({
    researchComponent,
    ...props
}: IResearchPreviewLayoutProps) => {
    const { isOptedIn } = useInfoArchOptIn();
    if (!isOptedIn || researchComponent === undefined) {
        return <ListPageLayout {...props} />;
    }

    return (
        <ResearchPreviewShell
            path={props.path}
            exact={props.exact}
            allJobsText={props.allJobsText}
            analyticsPageName={props.analyticsPageName}
            researchComponent={researchComponent}
        />
    );
};
